import React, { useState, useEffect } from "react";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import { Page, Document, PDFViewer } from "@react-pdf/renderer";
import { DEFAULT_COMPANY, formatDocDate } from "../../../../../utils/pdfDocumentUtils";
import {
  arPdfStyles,
  ArDocumentTitle,
  ArDocumentHeader,
  ArBuyerSiteSection,
  ArServicesTable,
  ArAmountInWords,
  ArHsnSummaryTable,
  ArBankSignatureFooter,
  ArPageNumber,
  ArRemarksSection,
} from "../../../../../components/pdf/ArDocumentPdfLayout";

const buildReceiptLineItems = (allocations = []) => {
  const rows = [];
  allocations.forEach((allocation) => {
    rows.push({
      description: `Payment against Invoice ${allocation.invoice_no || "N/A"} dated ${formatDocDate(allocation.invoice_date)}`,
      hsn_code: "-",
      quantity: 1,
      unit: "NOS",
      rate: allocation.allocated_amount,
      taxable_amount: parseFloat(allocation.allocated_amount) || 0,
      cgstAmt: 0,
      sgstAmt: 0,
      igstAmt: 0,
      itemTotal: parseFloat(allocation.allocated_amount) || 0,
    });

    (allocation.deductions || []).forEach((ded) => {
      const amount = parseFloat(ded.deduction_amount) || 0;
      rows.push({
        description: `${ded.deduction_type_name || ded.deduction_type_code || "Adjustment"}${ded.remarks ? ` — ${ded.remarks}` : ""}`,
        hsn_code: "-",
        quantity: 1,
        unit: "NOS",
        rate: amount,
        taxable_amount: amount,
        cgstAmt: 0,
        sgstAmt: 0,
        igstAmt: 0,
        itemTotal: amount,
      });
    });
  });
  return rows;
};

const ReceiptDocument = ({ allData, company, customer, site, project }) => {
  const receipt = allData?.receipt || {};
  const allocations = allData?.invoice_allocations || [];
  const receiptAmount = parseFloat(receipt.receipt_amount || receipt.amount || 0);
  const builtLineItems = buildReceiptLineItems(allocations);
  const lineItems =
    builtLineItems.length > 0
      ? builtLineItems
      : [
          {
            description: "Payment received from customer",
            hsn_code: "-",
            quantity: 1,
            unit: "NOS",
            rate: receiptAmount,
            taxable_amount: receiptAmount,
            cgstAmt: 0,
            sgstAmt: 0,
            igstAmt: 0,
            itemTotal: receiptAmount,
          },
        ];

  const detailRows = [
    { label: "Receipt No.", value: receipt.doc_no || receipt.receipt_no },
    { label: "Dated", value: formatDocDate(receipt.receipt_date) },
    { label: "Payment Mode", value: receipt.payment_mode },
    { label: "Reference No.", value: receipt.reference_no },
    { label: "Bank Name", value: receipt.bank_name },
    { label: "Branch Name", value: receipt.branch_name },
    { label: "Cheque Date", value: receipt.cheque_date ? formatDocDate(receipt.cheque_date) : null },
  ].filter((row) => row.value);

  return (
    <Document>
      <Page size="A4" style={arPdfStyles.page}>
        <ArDocumentTitle title="RECEIPT" subtitle="Payment Received from Customer" />
        <ArDocumentHeader company={company} detailRows={detailRows} />
        <ArBuyerSiteSection customer={customer} site={site} project={project} />
        <ArRemarksSection remarks={receipt.remarks} />
        <ArServicesTable
          lineItems={lineItems}
          doc={{ total_amount: receiptAmount }}
          descriptionHeader="Description of Payment / Adjustment"
        />
        <ArAmountInWords amount={receiptAmount} />
        <ArHsnSummaryTable enhancedItems={lineItems} doc={{}} />
        <ArBankSignatureFooter company={company} docTypeLabel="Receipt" />
        <ArPageNumber />
      </Page>
    </Document>
  );
};

export default function ReceiptPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [allData, setAllData] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [site, setSite] = useState(null);
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessError, setAccessError] = useState(null);

  useEffect(() => {
    const fetchReceiptData = async () => {
      try {
        const token = localStorage.getItem("zTaskAuthToken");
        const API_BASE = process.env.REACT_APP_NODE_SERVICE_BASE_URL || "http://localhost:8081";

        const receiptResponse = await fetch(`${API_BASE}/receipt/api/receipts/${id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (receiptResponse.status === 403) {
          const err = await receiptResponse.json();
          setAccessError(err.message || "PDF is available only after the receipt is approved.");
          return;
        }

        if (!receiptResponse.ok) {
          throw new Error("Failed to fetch receipt");
        }

        const pdfData = await receiptResponse.json();
        setAllData(pdfData);
        setCustomer(pdfData.customer);
        setSite(pdfData.site);
        setProject(pdfData.project);
        setCompany(DEFAULT_COMPANY);
      } catch (error) {
        console.error("Error fetching receipt data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchReceiptData();
    }
  }, [id]);

  if (isLoading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>Loading receipt data...</p>
      </div>
    );
  }

  if (accessError) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p style={{ marginBottom: "16px", color: "#c62828" }}>{accessError}</p>
        <button
          style={{ marginRight: "10px", padding: "8px 16px", background: "#6c757d", color: "white", border: "none", borderRadius: "4px" }}
          onClick={() => navigate("/ar/receipts")}
        >
          Back to Receipts
        </button>
        <button
          style={{ padding: "8px 16px", background: "#007bff", color: "white", border: "none", borderRadius: "4px" }}
          onClick={() => navigate(`/ar/receipts/${id}`)}
        >
          View Receipt
        </button>
      </div>
    );
  }

  if (!allData) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>Receipt not found.</p>
        <NavLink to="/ar/receipts">
          <button style={{ padding: "8px 16px", background: "#007bff", color: "white", border: "none", borderRadius: "4px" }}>
            Back to Receipts
          </button>
        </NavLink>
      </div>
    );
  }

  return (
    <>
      <NavLink to="/ar/receipts">
        <button style={{ marginBottom: "10px", padding: "8px 16px", background: "#6c757d", color: "white", border: "none", borderRadius: "4px" }}>
          Back To Receipts
        </button>
      </NavLink>
      <PDFViewer style={arPdfStyles.viewer}>
        <ReceiptDocument allData={allData} company={company} customer={customer} site={site} project={project} />
      </PDFViewer>
    </>
  );
}
